import { FC } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { getLanguageName } from 'i18n/i18n.utils';
import { LanguageCode } from 'i18n/internationalization.types';
import { StyledAlert } from './InvalidLanguageAlert.styled';
import AlertButton from './AlertButton';

interface InvalidLanguageAlertFormValues extends FieldValues {
  language: string;
  locales?: Record<string, unknown>;
}

interface InvalidLanguageAlertProps {
  i18nKey:
    | 'pageAutomations.systemTemplates.modal.invalidLocaleError'
    | 'pageAutomations.customTemplates.modal.invalidLocaleError';
}

const InvalidLanguageAlert: FC<InvalidLanguageAlertProps> = ({ i18nKey }) => {
  const { i18n } = useTranslation();
  const {
    formState: { errors },
    control,
    setValue,
  } = useFormContext<InvalidLanguageAlertFormValues>();
  const currentSelectedLanguage = useWatch({ control, name: 'language' });

  if (!errors.locales) {
    return null;
  }

  const invalidLanguages = Object.keys(errors.locales);
  const invalidInactiveLanguages = invalidLanguages.filter(
    (l) => l !== currentSelectedLanguage,
  );

  return (
    invalidInactiveLanguages.length > 0 && (
      <StyledAlert bsStyle="danger">
        <Trans
          i18nKey={i18nKey}
          values={{
            language: invalidInactiveLanguages
              .map((l) => getLanguageName(i18n.language, l as LanguageCode))
              .join(', '),
            count: invalidInactiveLanguages.length,
          }}
          components={{
            1: (
              <AlertButton
                onClick={() => {
                  setValue('language', invalidInactiveLanguages[0], {
                    shouldDirty: false,
                  });
                }}
              />
            ),
          }}
        />
      </StyledAlert>
    )
  );
};

export default InvalidLanguageAlert;
